import { createSceneCapabilities } from '@components/Flows/sceneCapabilities/index.js';
import timer_gen_2 from '@devices/settings/device_settings/timer_gen_2.js';
import schedule_gen_2 from '@devices/settings/device_settings/schedule_gen_2.js';
import webhooks_gen_2 from '@devices/settings/device_settings/webhooks_gen_2.js';
import scripts_gen_2 from '@devices/settings/device_settings/scripts_gen_2.js';
import networks_gen_2_pro from '@devices/settings/device_settings/networks_gen_2_pro.js';
import virtual_components_pro_devices from '@devices/settings/device_settings/virtual_components_pro_devices.js';
import safety_gen_2_auto_recovery_under_voltage from '@devices/settings/device_settings/safety_gen_2_auto_recovery_under_voltage.js';
import app_setting_pm_gen_2 from '@devices/settings/device_settings/app_setting_pm_gen_2.js';
import share from '@devices/settings/device_settings/share.js';
import multi_settings_gen_2 from '@devices/settings/device_settings/multi_settings_gen_2.js';
import roller_pro_pm_settings_gen_2 from '@devices/settings/common_settings/roller_pro_pm_settings_gen_2';
import bluetooth_bt_home_control_gen_2 from '@devices/settings/device_settings/bluetooth_bt_home_control_gen_2';
import addon_sensors, { addon_pro_devices } from '@devices/settings/device_settings/addon_sensors';
import bluetooth_gen_2 from '@devices/settings/device_settings/bluetooth_gen_2.js';

export default {
	name: 'ShellyPro2PM',
	friendly_name: 'Pro 2 PM',
	family: 'relay',
	isPM: true,
	addons: addon_pro_devices,
	modes: {
		relay: {
			channels: 2,
			notifications: [
				'offline',
				'device_problem',
				'overpower',
				'overvoltage',
				'forgotten_lights',
				'overtemperature',
			],
			settings: [
				timer_gen_2,
				schedule_gen_2,
				webhooks_gen_2,
				addon_sensors,
				scripts_gen_2,
				networks_gen_2_pro,
				bluetooth_bt_home_control_gen_2,
				virtual_components_pro_devices,
				safety_gen_2_auto_recovery_under_voltage,
				app_setting_pm_gen_2,
				share,
				multi_settings_gen_2,
			],
		},
		roller: {
			channels: 1,
			notifications: ['offline', 'device_problem', 'overpower', 'overvoltage', 'overtemperature'],
			slider: true,
			settings: roller_pro_pm_settings_gen_2,
		},
	},
	scene: {
		actions: {
			turn: {
				name: 'turn',
				description: 'Turn relay channel on or off',
				category: 'power',
				parameters: {
					value: {
						type: 'enum',
						required: true,
						enumValues: ['on', 'off', 'toggle'],
						description: 'Turn relay on, off, or toggle current state',
					},
					channel: {
						type: 'number',
						required: true,
						range: { min: 0, max: 1 },
						description: 'Relay channel (0 or 1 for dual relay)',
					},
				},
				validation: [
					{
						type: 'required',
						constraint: 'value',
						errorMessage: 'Turn action requires value parameter',
						severity: 'error',
					},
					{
						type: 'range',
						constraint: { min: 0, max: 1 },
						errorMessage: 'Channel must be 0 or 1 for dual relay',
						severity: 'error',
					},
				],
				commandTemplate: {
					endpoint: '/rpc/Switch.Set',
					method: 'POST',
					payload: {
						id: '{{channel}}',
						on: '{{turnValue}}',
						timeout: '{{timer}}',
					},
					responseExpected: true,
					errorHandling: 'retry',
				},
				supportedValues: {
					value: ['on', 'off', 'toggle'],
				},
				timerSupport: {
					minDuration: 1,
					maxDuration: 7200,
					units: ['seconds', 'minutes'],
					precision: 'second',
				},
				uiMetadata: {
					displayName: 'Turn Channel On/Off',
					icon: 'power',
					category: 'Control',
					order: 1,
					helpText: 'Turn specific relay channel on, off, or toggle its current state',
				},
			},
		},
		conditions: {
			is_on: {
				name: 'is_on',
				description: 'Check if relay channel is currently on',
				category: 'power',
				evaluationType: 'immediate',
				comparisonOperators: ['equals', 'not_equals'],
				parameters: {
					channel: {
						type: 'number',
						required: true,
						range: { min: 0, max: 1 },
						description: 'Relay channel to check',
					},
					value: {
						type: 'boolean',
						required: true,
						description: 'Expected relay state (true = on, false = off)',
					},
				},
				validation: [
					{
						type: 'required',
						constraint: 'value',
						errorMessage: 'State condition requires value parameter',
						severity: 'error',
					},
					{
						type: 'range',
						constraint: { min: 0, max: 1 },
						errorMessage: 'Channel must be 0 or 1 for dual relay',
						severity: 'error',
					},
				],
				stateProperties: {
					switch_state_0: {
						path: 'switch:0.output',
						type: 'boolean',
						description: 'Current relay channel 0 output state',
						updateFrequency: 1,
						caching: 'short',
					},
					switch_state_1: {
						path: 'switch:1.output',
						type: 'boolean',
						description: 'Current relay channel 1 output state',
						updateFrequency: 1,
						caching: 'short',
					},
				},
				uiMetadata: {
					displayName: 'Channel Is On',
					icon: 'toggle-on',
					category: 'State',
					order: 1,
					helpText: 'Check if a specific relay channel is currently turned on',
				},
			},
			power_consumption: {
				name: 'power_consumption',
				description: 'Check current power consumption for channel',
				category: 'energy',
				evaluationType: 'immediate',
				comparisonOperators: [
					'greater_than',
					'less_than',
					'greater_equal',
					'less_equal',
					'in_range',
				],
				parameters: {
					channel: {
						type: 'number',
						required: true,
						range: { min: 0, max: 1 },
						description: 'Relay channel to monitor',
					},
					threshold: {
						type: 'number',
						required: true,
						range: { min: 0, max: 3680 },
						unit: 'W',
						description: 'Power threshold in watts',
					},
					operator: {
						type: 'enum',
						required: true,
						enumValues: ['greater_than', 'less_than', 'greater_equal', 'less_equal'],
						description: 'Comparison operator',
					},
				},
				validation: [
					{
						type: 'range',
						constraint: { min: 0, max: 3680 },
						errorMessage: 'Power threshold must be between 0 and 3680 watts',
						severity: 'error',
					},
				],
				stateProperties: {
					power_0: {
						path: 'switch:0.apower',
						type: 'number',
						unit: 'W',
						description: 'Current active power consumption channel 0',
						updateFrequency: 5,
						caching: 'medium',
					},
					power_1: {
						path: 'switch:1.apower',
						type: 'number',
						unit: 'W',
						description: 'Current active power consumption channel 1',
						updateFrequency: 5,
						caching: 'medium',
					},
				},
				thresholdSupport: {
					supportedOperators: ['greater_than', 'less_than', 'greater_equal', 'less_equal'],
					hysteresis: 5,
					debounceTime: 1000,
				},
				uiMetadata: {
					displayName: 'Channel Power Consumption',
					icon: 'bolt',
					category: 'Energy',
					order: 2,
					helpText: 'Monitor power consumption levels for specific channel',
				},
			},
		},
		triggers: {
			state_changed: {
				name: 'state_changed',
				description: 'Triggered when relay channel state changes',
				category: 'power',
				triggerType: 'state_change',
				parameters: {
					channel: {
						type: 'number',
						required: true,
						range: { min: 0, max: 1 },
						description: 'Relay channel to monitor',
					},
					from_state: {
						type: 'enum',
						required: false,
						enumValues: ['on', 'off', 'any'],
						defaultValue: 'any',
						description: 'Previous state to trigger from',
					},
					to_state: {
						type: 'enum',
						required: false,
						enumValues: ['on', 'off', 'any'],
						defaultValue: 'any',
						description: 'New state to trigger to',
					},
				},
				validation: [
					{
						type: 'range',
						constraint: { min: 0, max: 1 },
						errorMessage: 'Channel must be 0 or 1 for dual relay',
						severity: 'error',
					},
				],
				eventProperties: {
					state_change: {
						eventName: 'switch.state_changed',
						dataType: 'object',
						description: 'Relay channel state change event',
						frequency: 'medium',
					},
				},
				filterSupport: {
					supportedFilters: ['value_filter', 'time_filter'],
					maxFilters: 3,
					filterLogic: 'AND',
				},
				uiMetadata: {
					displayName: 'Channel State Changed',
					icon: 'exchange-alt',
					category: 'Events',
					order: 1,
					helpText: 'Trigger when specific relay channel turns on or off',
				},
			},
			power_threshold: {
				name: 'power_threshold',
				description: 'Triggered when channel power crosses threshold',
				category: 'energy',
				triggerType: 'value_change',
				parameters: {
					channel: {
						type: 'number',
						required: true,
						range: { min: 0, max: 1 },
						description: 'Relay channel to monitor',
					},
					threshold: {
						type: 'number',
						required: true,
						range: { min: 0, max: 3680 },
						unit: 'W',
						description: 'Power threshold in watts',
					},
					direction: {
						type: 'enum',
						required: true,
						enumValues: ['above', 'below', 'both'],
						defaultValue: 'both',
						description: 'Threshold crossing direction',
					},
				},
				validation: [
					{
						type: 'range',
						constraint: { min: 0, max: 3680 },
						errorMessage: 'Power threshold must be between 0 and 3680 watts',
						severity: 'error',
					},
				],
				eventProperties: {
					power_change: {
						eventName: 'switch.power_changed',
						dataType: 'number',
						description: 'Power value change event',
						frequency: 'high',
					},
				},
				filterSupport: {
					supportedFilters: ['value_filter', 'duration_filter'],
					maxFilters: 2,
					filterLogic: 'AND',
				},
				uiMetadata: {
					displayName: 'Channel Power Threshold',
					icon: 'chart-line',
					category: 'Energy',
					order: 2,
					helpText: 'Trigger when channel power consumption crosses a threshold',
				},
			},
		},
	},
	// Use template-based sceneCapabilities for maintainability
	sceneCapabilities: createSceneCapabilities({
		template: 'dual_relay',
	}),
};
